import React, { useEffect, useRef, useState } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Heart, Sparkles, Send, User, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { avatarUrl } from '../utils/avatar';
import { WORLD_CONFIG } from '../lib/worldScenes';

const STAGE_LABEL = ['Awakening', 'Stirring', 'Flourishing', 'Thriving'];

// One drifting "traveller" — yourself, an opted-in avatar+name, or an
// anonymised silhouette. Each ambles across the scene on its own cadence.
const Traveller = ({ t, color, idx }) => {
  const [imgOk, setImgOk] = React.useState(true);
  const col = idx % 3;
  const row = Math.floor(idx / 3);
  const left = 10 + col * 28;          // 10,38,66 %
  const bottom = 10 + row * 58;        // stacked rows, more vertical breathing room
  const delay = `${(idx % 5) * 0.9}s`;
  const duration = `${6 + (idx % 4)}s`;  // 6–9s, varied so the crowd feels alive
  const isYou = t.kind === 'you';
  const photo = isYou && imgOk ? t.photo : null;
  return (
    <div
      className="absolute flex flex-col items-center village-traveller"
      style={{ left: `${left}%`, bottom: `${bottom}px`, animationDelay: delay, animationDuration: duration }}
      data-testid={isYou ? 'village-traveller-you' : `village-traveller-${t.kind}`}
    >
      {isYou && photo ? (
        <span className="relative">
          <img src={photo} alt="you" onError={() => setImgOk(false)} className="w-9 h-9 rounded-full object-cover shadow-lg" style={{ border: `2px solid ${color}` }} />
        </span>
      ) : isYou || t.kind === 'optin' ? (
        <span aria-hidden="true" className="w-8 h-8 rounded-full grid place-items-center font-bold text-black text-xs shadow-lg" style={{ background: color, boxShadow: isYou ? `0 0 0 2px ${color}, 0 0 14px ${color}80` : undefined }}>
          {(t.name || 'U').charAt(0)}
        </span>
      ) : (
        <span className="w-8 h-8 rounded-full grid place-items-center bg-black/70 border border-white/20 shadow-lg">
          <User className="w-4 h-4 text-white/60" />
        </span>
      )}
      {(isYou || t.kind === 'optin') && (
        <span
          className="mt-1 px-2 py-0.5 rounded-full backdrop-blur text-[9px] whitespace-nowrap"
          style={isYou ? { background: color, color: '#000', fontWeight: 700 } : { background: 'rgba(0,0,0,0.7)', color: 'rgba(255,255,255,0.9)' }}
        >
          {isYou ? 'You' : t.name}
        </span>
      )}
    </div>
  );
};

// A lightweight CSS confetti burst — no library. Renders when a stage unlocks.
const CONFETTI_COLORS = ['#CCFF00', '#9BE33B', '#5FC6E8', '#E0B96A', '#A9A7E8', '#ffffff'];
const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden z-20" data-testid="village-confetti">
    {Array.from({ length: 36 }).map((_, i) => (
      <span
        key={i}
        className="confetti-piece"
        style={{
          left: `${(i * 2.7 + (i % 5) * 3) % 100}%`,
          background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          animationDelay: `${(i % 8) * 0.12}s`,
          animationDuration: `${2.2 + (i % 5) * 0.25}s`,
        }}
      />
    ))}
  </div>
);

export default function Village() {
  const { pillar } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const config = WORLD_CONFIG[pillar];
  const [v, setV] = useState(null);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const loadedPillar = useRef(null);

  useEffect(() => {
    if (loadedPillar.current === pillar) return;  // dedupe (StrictMode/param re-render)
    loadedPillar.current = pillar;
    api.getVillage(pillar).then((r) => {
      setV(r.data);
      if (r.data?.stage_up) {
        setCelebrate(true);
        setTimeout(() => setCelebrate(false), 6000);
      }
    }).catch(() => {});
  }, [pillar]);

  if (!config) return <Navigate to="/dashboard" replace />;
  const color = config.color;

  const post = async () => {
    const t = text.trim();
    if (!t) return;
    setPosting(true);
    try {
      const r = await api.addVillageWisdom(pillar, t);
      setV((prev) => ({ ...prev, wisdom: [r.data, ...(prev?.wisdom || [])] }));
      setText('');
      toast.success('Wisdom shared anonymously 🌱');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not share that.');
    } finally { setPosting(false); }
  };

  const heart = async (id) => {
    try {
      const r = await api.heartVillageWisdom(id);
      setV((prev) => ({ ...prev, wisdom: (prev?.wisdom || []).map((w) => (w.id === id ? r.data : w)) }));
    } catch (e) { /* ignore */ }
  };

  const scenery = v ? `/assets/living/worlds/${pillar}_${v.scenery_state}.webp` : null;
  const prevScenery = v ? `/assets/living/worlds/${pillar}_${v.previous_scenery_state}.webp` : null;

  // You always appear in your own village, gently moving through the scene.
  const youTraveller = {
    kind: 'you',
    name: (user?.name || 'You').split(' ')[0],
    photo: avatarUrl(user, token),
  };
  const travellers = [youTraveller, ...((v?.travellers) || [])].slice(0, 7);

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto pb-8" data-testid="village-page">
        <button onClick={() => navigate(`/world/${pillar}`)} data-testid="village-back" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-3">
          <ArrowLeft className="w-4 h-4" /> Back to your world
        </button>

        {/* Scenery hero — evolves with the collective goal; travellers drift across.
            On a stage unlock the old skin fades to the new one + confetti. */}
        <div className="relative w-full aspect-[16/10] rounded-3xl overflow-hidden border border-white/10" data-testid="village-hero">
          {/* previous skin sits underneath; the new skin reveals over it on unlock */}
          {celebrate && prevScenery && <img src={prevScenery} alt="" className="absolute inset-0 w-full h-full object-cover" />}
          {scenery && <img src={scenery} alt="" className={`absolute inset-0 w-full h-full object-cover ${celebrate ? 'village-reveal' : ''}`} />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40" />
          <div className="absolute top-4 left-4">
            <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color }}>
              <Users className="w-3.5 h-3.5" /> Village
            </span>
            <h1 className="font-secondary font-bold text-3xl text-white leading-tight mt-0.5">{v?.name || config.label}</h1>
            {v && <span className="text-xs text-zinc-300">{STAGE_LABEL[v.stage]} · grows as the community contributes</span>}
          </div>
          {travellers.map((t, i) => <Traveller key={i} t={t} color={color} idx={i} />)}
          {celebrate && <Confetti />}
          {celebrate && v && (
            <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 z-30 village-banner-in" data-testid="village-unlock-banner">
              <div className="rounded-2xl px-4 py-3 flex items-center gap-3 shadow-xl backdrop-blur" style={{ background: 'rgba(0,0,0,0.72)', border: `1px solid ${color}` }}>
                <PartyPopper className="w-5 h-5 shrink-0" style={{ color }} />
                <p className="text-sm text-white leading-snug">
                  <span className="font-bold" style={{ color }}>{v.name} reached {STAGE_LABEL[v.stage]}</span> — you helped grow this 🌱
                </p>
                <button onClick={() => setCelebrate(false)} data-testid="village-unlock-dismiss" className="ml-auto text-zinc-400 hover:text-white text-xs shrink-0">✕</button>
              </div>
            </div>
          )}
        </div>

        {/* Collective goal — monthly cycle */}
        {v && (
          <div className="hpme-card p-5 mt-4" data-testid="village-goal">
            <div className="flex items-baseline justify-between">
              <h3 className="font-secondary font-bold text-lg text-white">{v.period_label} Goal</h3>
              <span className="hpme-number text-sm" style={{ color }}>{v.current} / {v.target} {v.unit}</span>
            </div>
            <div className="h-2.5 w-full bg-white/10 rounded-full overflow-hidden mt-3">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(2, v.pct)}%`, background: color }} data-testid="village-goal-bar" />
            </div>
            <p className="text-xs text-zinc-400 mt-2" data-testid="village-contributors">
              <span className="text-white font-semibold">{v.contributors}</span> {v.contributors === 1 ? 'person is' : 'people are'} growing this world · you've contributed <span className="text-white font-semibold">{v.your_contribution}</span> this month · <span className="text-white font-semibold">{v.days_left}</span> {v.days_left === 1 ? 'day' : 'days'} left.
            </p>
            {v.last_month > 0 && (
              <p className="text-xs text-zinc-500 mt-1.5" data-testid="village-last-month">
                Last month the village completed <span className="text-zinc-300 font-semibold">{v.last_month}</span> {v.unit} 🎉
              </p>
            )}
          </div>
        )}

        {/* Anonymous wisdom */}
        <div className="hpme-card p-5 mt-4" data-testid="village-wisdom">
          <h3 className="font-secondary font-bold text-lg text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4" style={{ color }} /> Wisdom from the Village
          </h3>
          <p className="text-xs text-zinc-500 mt-0.5 mb-3">Anonymous reflections. No names, no scores — just what's helped.</p>
          <div className="flex items-end gap-2 mb-4">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={240}
              rows={2}
              placeholder="Share something that helped you…"
              data-testid="village-wisdom-input"
              className="flex-1 resize-none rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-base text-white placeholder:text-zinc-500 focus:outline-none focus:border-white/30"
            />
            <button onClick={post} disabled={posting || !text.trim()} data-testid="village-wisdom-post" className="rounded-xl px-3 py-2.5 text-black font-medium disabled:opacity-40 shrink-0" style={{ background: color }}>
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-2.5">
            {(v?.wisdom || []).map((w) => (
              <div key={w.id} className="flex items-start justify-between gap-3 p-3 rounded-xl bg-white/5 border border-white/5" data-testid="village-wisdom-item">
                <p className="text-sm text-zinc-200 leading-snug">{w.text}</p>
                <button onClick={() => heart(w.id)} data-testid="village-wisdom-heart" className="flex items-center gap-1 text-xs text-zinc-400 hover:text-pink-400 shrink-0 transition-colors">
                  <Heart className="w-3.5 h-3.5" /> {w.hearts || 0}
                </button>
              </div>
            ))}
            {v && (v.wisdom || []).length === 0 && (
              <p className="text-sm text-zinc-500 text-center py-3">Be the first to leave some wisdom here.</p>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
